// chat-overlay.ts — the ONE chat surface for the whole web client: a full-screen,
// WI-themed IChatController used by every MP chat path (the DOM lobby's room /
// create-game / staging screens AND in-game), replacing the legacy white
// #chat-container overlay (web/chat.ts, a port of webchatcontroller.cpp).
//
// It is the embedder's IChatController (HostGetChatController) — the SAME single
// instance threaded to both the in-game Chatter (gamesession) and the DOM-lobby
// Chatter (lobby-controller). The Chatter mediator owns the open/blink/title
// bookkeeping; this overlay just renders the log + input and reports the user's
// SEND / DONE / PLAYERS actions back through IChatControllerCallback.
//
// Styled from the DOM design system (chrome.ts): the dark COL_BG backdrop, the WI
// title face (formTitle), 9-slice WI buttons (makeStageButton), the shared accent
// text input (stageTextInput), all at the design-stage magnification (formScale).
// Being genuinely full-screen, the content layer honours the device SAFE-AREA
// insets (viewport-fit=cover is set in pages/public/index.html) so the title and
// the bottom input row clear the notch / home indicator — the legacy overlay,
// rebuilt edge-to-edge, did not.

import type { IChatController, IChatControllerCallback } from '../ui/chatter.ts';
import type { ButtonArt } from './title-art.ts';
import {
  COL_BG,
  COL_FG,
  COL_ACCENT,
  TEXT_SHADOW,
  FONT_BODY,
  el,
  formScale,
  formTitle,
  makeStageButton,
  buttonWidthU,
  stageTextInput,
  ensureA11yStyles,
  mountViewportFit,
  type ViewportFit,
  withFocusPreserved,
} from './chrome.ts';

export interface ChatOverlayDeps {
  /** the document to build into (defaults to the global document; injectable for tests). */
  doc?: Document;
  /** the 9-slice button art — a GETTER because the overlay is constructed before the
   *  art is extracted (main.ts), and re-resolved each time the overlay re-lays-out. */
  buttonArt?: () => ButtonArt | undefined;
  /** play a GUI sfx (ksfxGui*) on press — wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
}

interface ChatLine {
  player: string;
  chat: string;
}

export class ChatOverlay implements IChatController {
  private cb: IChatControllerCallback | null = null;
  private title_ = 'Chat';
  private readonly doc: Document;
  private readonly deps: ChatOverlayDeps;
  private lines: ChatLine[] = [];
  private visible = false;
  /** the window WIDTH at the last chrome build, so re-showing the overlay skips a rebuild
   *  when nothing about the scale changed. (The RESIZE side of that rule now lives in
   *  chrome.mountViewportFit.) */
  private lastWidth = 0;

  /** the viewport binding — assigned on first build (ensureBuilt). */
  private viewport!: ViewportFit;

  private container: HTMLDivElement | null = null;
  private content: HTMLDivElement | null = null;
  private titleEl: HTMLElement | null = null;
  private logEl: HTMLDivElement | null = null;
  private input: HTMLInputElement | null = null;

  constructor(deps: ChatOverlayDeps = {}) {
    this.deps = deps;
    this.doc = deps.doc ?? document;
  }

  // a 9-slice ButtonControl factory bound to the CURRENT button art (resolved late) +
  // the host sfx. Re-made per layout so art that loads after construction is picked up.
  private makeBtn(): ReturnType<typeof makeStageButton> {
    return makeStageButton({ buttonArt: this.deps.buttonArt?.(), playSfx: this.deps.playSfx });
  }

  // build the persistent container once (lazily, on first Show — so the button-art
  // getter has resolved by then): an edge-to-edge COL_BG backdrop (fills under the
  // notch) + a safe-area-padded content layer that holds the per-layout chrome.
  private ensureBuilt(): void {
    if (this.container !== null) return;
    ensureA11yStyles();
    const d = this.doc;
    const container = d.createElement('div');
    Object.assign(container.style, {
      // sized to the VISUAL viewport by chrome.mountViewportFit (left/top/width/height) so when the
      // soft keyboard opens the overlay shrinks to the area above it.
      position: 'fixed',
      left: '0',
      top: '0',
      width: '100%',
      height: '100%',
      zIndex: '40',
      display: 'none',
      background: COL_BG,
    });
    const content = d.createElement('div');
    Object.assign(content.style, {
      position: 'absolute',
      inset: '0',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      fontFamily: FONT_BODY,
      color: COL_FG,
      // SAFE AREAS — the reason this overlay isn't a bare inset:0 box. The same idiom the
      // shell already uses (menu-view.ts / install-prompt.ts): base margin + env() inset,
      // with a 0px fallback for browsers / the node test env without env().
      paddingTop: 'calc(8px + env(safe-area-inset-top, 0px))',
      paddingRight: 'calc(8px + env(safe-area-inset-right, 0px))',
      paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
      paddingLeft: 'calc(8px + env(safe-area-inset-left, 0px))',
    });
    container.appendChild(content);
    d.body.appendChild(container);
    this.container = container;
    this.content = content;
    // The same keyboard-safety contract the lobby / community / DLC shells use: a WIDTH change
    // is a real orientation/window resize → rebuild at the new scale; a height-only change (or
    // any visualViewport change) is the soft keyboard → re-fit only, so the focused input is
    // never destroyed. This overlay is the surface the rule was first worked out on.
    //
    // No detach: unlike the views, this overlay is a singleton built once and never destroyed.
    this.viewport = mountViewportFit(container, {
      rebuild: () => {
        this.layout();
        this.viewport.fit();
      },
      stage: () => null,
      skip: () => !this.visible,
    });
  }

  // Size the overlay to the VISUAL viewport (the area the soft keyboard does NOT cover)
  // rather than the layout viewport, so the bottom input row sits just above the keyboard —
  // WITHOUT rebuilding the DOM, so the focused input keeps focus.
  private fitViewport(): void {
    this.viewport.fit();
  }

  // (re)build the inner chrome at the current scale: PLAYERS · title · DONE header,
  // a scrolling full-history log, and an input + SEND row. Cheap; runs on show + resize
  // (NOT on AddChat — that appends a single line so typing focus is never lost).
  private layout(): void {
    if (this.content === null) return;
    const s = formScale();
    const btn = this.makeBtn();
    const content = this.content;
    content.style.gap = `${4 * s}px`;

    const hU = 13; // header button height (design units)
    const players = btn('PLAYERS', () => this.onPlayers(), {
      w: buttonWidthU('PLAYERS', hU, s) * s,
      h: hU * s,
    });
    const title = formTitle(this.title_, s);
    Object.assign(title.style, { flex: '1', minWidth: '0' });
    this.titleEl = title;
    const done = btn('DONE', () => this.onHide(), {
      w: buttonWidthU('DONE', hU, s) * s,
      h: hU * s,
    });
    const header = el('div', {
      display: 'flex',
      alignItems: 'center',
      gap: `${4 * s}px`,
      flex: '0 0 auto',
    });
    header.append(players, title, done);

    const log = el('div', {
      flex: '1',
      minHeight: '0',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      background: 'rgba(7,22,27,0.55)',
      border: `${Math.max(1, Math.round(s))}px solid rgba(143,208,224,0.35)`,
      padding: `${2 * s}px ${4 * s}px`,
      fontSize: `${Math.round(5.5 * s)}px`,
      lineHeight: '1.5',
      textAlign: 'left',
      textShadow: TEXT_SHADOW,
    });
    log.dataset.list = '1';
    this.logEl = log;
    for (const ln of this.lines) log.appendChild(this.renderLine(ln, s));

    const prev = this.input?.value ?? '';
    const input = stageTextInput('chatMessage', 'Type a message…', prev, s);
    input.style.flex = '1';
    input.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') {
        this.onSend();
        e.preventDefault();
      }
    });
    this.input = input;
    const send = btn('SEND', () => this.onSend(), {
      w: buttonWidthU('SEND', 14, s) * s,
      h: 14 * s,
    });
    const row = el('div', { display: 'flex', gap: `${4 * s}px`, flex: '0 0 auto' });
    row.append(input, send);

    // preserve typing focus + caret across the rebuild — an orientation change can rebuild
    // while the user is mid-message, and replaceChildren would otherwise drop focus.
    withFocusPreserved(content, () => {
      content.replaceChildren(header, log, row);
    });
    log.scrollTop = log.scrollHeight; // open / re-layout pinned to the newest line
  }

  // one log line: "player: chat" (accent name) or a dim italic system line ('' player).
  private renderLine(ln: ChatLine, s: number): HTMLElement {
    const div = el('div', {
      display: 'block',
      marginBottom: `${1 * s}px`,
      wordBreak: 'break-word',
    });
    if (ln.player.length > 0) {
      const name = el('span', { color: COL_ACCENT, fontWeight: '700' });
      name.textContent = `${ln.player}: `;
      const txt = el('span', { color: COL_FG });
      txt.textContent = ln.chat;
      div.append(name, txt);
    } else {
      const txt = el('span', { color: COL_FG, opacity: '0.65', fontStyle: 'italic' });
      txt.textContent = ln.chat;
      div.append(txt);
    }
    return div;
  }

  // webchatcontroller.cpp:90-104 onShow
  private onShow(): void {
    this.ensureBuilt();
    if (this.visible) return;
    this.visible = true;
    if (this.input === null || window.innerWidth !== this.lastWidth) {
      this.lastWidth = window.innerWidth;
      this.layout();
    }
    this.fitViewport();
    if (this.container !== null) this.container.style.display = 'block';
    try {
      this.input?.focus();
    } catch {
      /* detached */
    }
  }

  // webchatcontroller.cpp:106-112 onHide → OnChatDismissed
  private onHide(): void {
    if (!this.visible) return;
    this.visible = false;
    if (this.container !== null) this.container.style.display = 'none';
    this.cb?.OnChatDismissed();
  }

  // webchatcontroller.cpp:114-124 onSend → OnChatSend
  private onSend(): void {
    if (this.input === null) return;
    const chat = this.input.value;
    if (chat.length === 0) return;
    this.input.value = '';
    this.cb?.OnChatSend(chat);
    try {
      this.input.focus();
    } catch {
      /* detached */
    }
  }

  // webchatcontroller.cpp:138-143 onPlayers → OnPlayers
  private onPlayers(): void {
    this.cb?.OnPlayers();
    try {
      this.input?.focus();
    } catch {
      /* detached */
    }
  }

  // ── IChatController ──────────────────────────────────────────────────────────
  // webchatcontroller.cpp:34-41 Clear
  Clear(): void {
    this.lines = [];
    if (this.logEl !== null) this.logEl.replaceChildren();
    if (this.input !== null) this.input.value = '';
  }

  // webchatcontroller.cpp:43-71 AddChat — append a single line (NO re-layout, so the
  // user's typing focus + caret survive an incoming line), then stick to the bottom.
  AddChat(player: string, chat: string): void {
    this.lines.push({ player, chat });
    if (this.logEl !== null) {
      this.logEl.appendChild(this.renderLine({ player, chat }, formScale()));
      this.logEl.scrollTop = this.logEl.scrollHeight;
    }
  }

  // webchatcontroller.cpp:73-165 Show
  Show(fShow: boolean): void {
    if (fShow) this.onShow();
    else this.onHide();
  }

  // webchatcontroller.cpp:167-173
  SetTitle(title: string): void {
    this.title_ = title;
    if (this.titleEl !== null) this.titleEl.textContent = title;
  }
  GetTitle(): string {
    return this.title_;
  }

  // webchatcontroller.cpp:175-180
  SetCallback(pcccb: IChatControllerCallback | null): IChatControllerCallback | null {
    const old = this.cb;
    this.cb = pcccb;
    return old;
  }
}
